import { Injectable } from "@angular/core";
import { AuthService } from '@core/auth.service';

/**
 * SafeStorageService provides safe access to localStorage and sessionStorage
 * with proper null checks and error handling to prevent crashes in browsers
 * where storage is unavailable, disabled, or in private browsing mode.
 */
@Injectable({
  providedIn: "root",
})
export class SafeStorageService {

  constructor(private authService: AuthService) {}
  /**
   * Checks if localStorage is available and functional
   */
  private isLocalStorageAvailable(): boolean {
    try {
      if (typeof localStorage === 'undefined' || localStorage === null) {
        return false;
      }
      // Test if we can actually use localStorage
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      // localStorage is not available or quota exceeded
      return false;
    }
  }

  /**
   * Checks if sessionStorage is available and functional
   */
  private isSessionStorageAvailable(): boolean {
    try {
      if (typeof sessionStorage === 'undefined' || sessionStorage === null) {
        return false;
      }
      // Test if we can actually use sessionStorage
      const testKey = '__sessionStorage_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch (e) {
      // sessionStorage is not available or quota exceeded
      return false;
    }
  }

  /**
   * Safely get an item from localStorage
   * @param key - The key to retrieve
   * @param defaultValue - Optional default value to return if key doesn't exist or storage unavailable
   * @returns The stored value or null/defaultValue
   */
  getItem(key: string, defaultValue: string | null = null): string | null {
    try {
      if (!this.isLocalStorageAvailable()) {
        console.warn(`localStorage is unavailable. Returning default value for key: ${key}`);
        return defaultValue;
      }
      const value = localStorage.getItem(key);
      return value !== null ? value : defaultValue;
    } catch (e) {
      this.authService.errorHandlerLogs(`Error accessing localStorage for key: ${key}`, e);
      return defaultValue;
    }
  }

  /**
   * Safely set an item in localStorage
   * @param key - The key to set
   * @param value - The value to store
   * @returns true if successful, false otherwise
   */
  setItem(key: string, value: string): boolean {
    try {
      if (!this.isLocalStorageAvailable()) {
        console.warn(`localStorage is unavailable. Cannot set key: ${key}`);
        return false;
      }
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      this.authService.errorHandlerLogs(`Error setting localStorage for key: ${key}`, e);
      return false;
    }
  }

  /**
   * Safely remove an item from localStorage
   * @param key - The key to remove
   * @returns true if successful, false otherwise
   */
  removeItem(key: string): boolean {
    try {
      if (!this.isLocalStorageAvailable()) {
        console.warn(`localStorage is unavailable. Cannot remove key: ${key}`);
        return false;
      }
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      this.authService.errorHandlerLogs(`Error removing from localStorage for key: ${key}`, e);
      return false;
    }
  }

  /**
   * Safely clear all items from localStorage
   * @returns true if successful, false otherwise
   */
  clear(): boolean {
    try {
      if (!this.isLocalStorageAvailable()) {
        console.warn(`localStorage is unavailable. Cannot clear storage.`);
        return false;
      }
      localStorage.clear();
      return true;
    } catch (e) {
      this.authService.errorHandlerLogs(`Error clearing localStorage`, e);
      return false;
    }
  }

  /**
   * Safely get an item from sessionStorage
   * @param key - The key to retrieve
   * @param defaultValue - Optional default value to return if key doesn't exist or storage unavailable
   * @returns The stored value or null/defaultValue
   */
  getSessionItem(key: string, defaultValue: string | null = null): string | null {
    try {
      if (!this.isSessionStorageAvailable()) {
        console.warn(`sessionStorage is unavailable. Returning default value for key: ${key}`);
        return defaultValue;
      }
      const value = sessionStorage.getItem(key);
      return value !== null ? value : defaultValue;
    } catch (e) {
      this.authService.errorHandlerLogs(`Error accessing sessionStorage for key: ${key}`, e);
      return defaultValue;
    }
  }

  /**
   * Safely set an item in sessionStorage
   * @param key - The key to set
   * @param value - The value to store
   * @returns true if successful, false otherwise
   */
  setSessionItem(key: string, value: string): boolean {
    try {
      if (!this.isSessionStorageAvailable()) {
        console.warn(`sessionStorage is unavailable. Cannot set key: ${key}`);
        return false;
      }
      sessionStorage.setItem(key, value);
      return true;
    } catch (e) {
      this.authService.errorHandlerLogs(`Error setting sessionStorage for key: ${key}`, e);
      return false;
    }
  }

  /**
   * Safely remove an item from sessionStorage
   * @param key - The key to remove
   * @returns true if successful, false otherwise
   */
  removeSessionItem(key: string): boolean {
    try {
      if (!this.isSessionStorageAvailable()) {
        console.warn(`sessionStorage is unavailable. Cannot remove key: ${key}`);
        return false;
      }
      sessionStorage.removeItem(key);
      return true;
    } catch (e) {
      this.authService.errorHandlerLogs(`Error removing from sessionStorage for key: ${key}`, e);
      return false;
    }
  }

  /**
   * Check if localStorage is currently available
   * @returns true if localStorage is available, false otherwise
   */
  isStorageAvailable(): boolean {
    return this.isLocalStorageAvailable();
  }
}